/**
 * Typed wrappers for the read-only mobile fleet telemetry endpoints
 * (viq-backend apps/superadmin-console/src/handlers/telemetry.ts, gated on
 * `read User`). The shapes mirror that app's src/db/deviceTelemetry.ts.
 *
 * Console-only on purpose: these read every tenant's device state, so there
 * is no portal counterpart and nothing here belongs in packages/*.
 */
import { superadminFetch } from './dataProvider';

export const TELEMETRY_EVENT_NAMES = [
  'session_start',
  'app_state',
  'sync_cycle',
  'sync_health',
  'js_error',
  'auth_event',
] as const;
export type TelemetryEventName = (typeof TELEMETRY_EVENT_NAMES)[number];

export interface SyncDay {
  /** Calendar day in the requested time zone, YYYY-MM-DD. */
  day: string;
  ok: number;
  failed: number;
}

export interface VersionCount {
  appVersion: string | null;
  platform: string | null;
  installs: number;
}

export interface TelemetrySummary {
  days: number;
  tz: string;
  /** Highest dotted-numeric version seen anywhere in the fleet. */
  latestVersion: string | null;
  syncDaily: SyncDay[];
  /** Installs seen in the last 30 days, newest version first. */
  versions: VersionCount[];
}

export interface DeviceInstall {
  installId: string;
  oid: string;
  userName: string | null;
  userEmail: string | null;
  companyId: string | null;
  companyName: string | null;
  platform: string | null;
  osVersion: string | null;
  appVersion: string | null;
  build: string | null;
  otaUpdateId: string | null;
  firstSeenAt: string;
  lastSeenAt: string;
  lastOpenedAt: string | null;
  lastSyncOkAt: string | null;
  lastSyncFailAt: string | null;
  lastSyncErrorClass: string | null;
  consecutiveSyncFailures: number;
  unsyncedCount: number | null;
  oldestUnsyncedAt: string | null;
  /** Unsynced rows on the device and no successful sync in 24 h (or ever). */
  stranded: boolean;
  /** Below the fleet's latest version. */
  outdated: boolean;
}

export interface DeviceEvent {
  id: string;
  name: string;
  occurredAt: string;
  receivedAt: string;
  props: Record<string, string | number | boolean>;
}

/** The browser's IANA zone, so the daily chart's days are the viewer's days. */
export function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/** GET /v1/superadmin/telemetry/summary — daily sync outcomes + version mix. */
export async function fetchTelemetrySummary(opts: {
  companyId: string | null;
  days: number;
  tz: string;
}): Promise<TelemetrySummary> {
  const params = new URLSearchParams({ days: String(opts.days), tz: opts.tz });
  if (opts.companyId) params.set('companyId', opts.companyId);
  return superadminFetch<TelemetrySummary>(`/v1/superadmin/telemetry/summary?${params}`);
}

/** GET /v1/superadmin/telemetry/installs — one row per install. */
export async function fetchTelemetryInstalls(opts: {
  companyId: string | null;
}): Promise<{ latestVersion: string | null; installs: DeviceInstall[] }> {
  const params = new URLSearchParams();
  if (opts.companyId) params.set('companyId', opts.companyId);
  const qs = params.toString();
  return superadminFetch(`/v1/superadmin/telemetry/installs${qs ? `?${qs}` : ''}`);
}

/** GET /v1/superadmin/telemetry/installs/{installId}/events — newest first. */
export async function fetchInstallEvents(
  installId: string,
  opts: { name?: TelemetryEventName; limit?: number } = {},
): Promise<{ events: DeviceEvent[] }> {
  const params = new URLSearchParams();
  if (opts.name) params.set('name', opts.name);
  if (opts.limit) params.set('limit', String(opts.limit));
  const qs = params.toString();
  return superadminFetch(
    `/v1/superadmin/telemetry/installs/${encodeURIComponent(installId)}/events${qs ? `?${qs}` : ''}`,
  );
}
